/**
 * O que faz: Define tipos TypeScript usados pelo sistema de follow-up e reativação.
 * Onde usa: Hooks e componentes de configuração de follow-up.
 * Conexões: Tabelas follow_up_campaigns, follow_up_templates e follow_up_execucoes.
 * Tabelas: Apenas representa campos lidos/escritos; não executa operações no banco.
 * Autor: IA
 * Data: 23/04/2026
 */

export type FollowupTriggerType =
  | 'inatividade_geral'
  | 'pergunta_sem_resposta'
  | 'mensagem_marcada'
  | 'manual';

// Como comparar o padrao_gatilho_texto com o conteúdo da mensagem
export type FollowupMatchType = 'contains' | 'equals';

// Tipo de geração de mensagem por template
export type FollowupMensagemTipo = 'manual' | 'ai_dinamico';

// Define se o envio usa um horario fixo diario ou acontece depois do tempo sem resposta.
export type FollowupModoEnvio = 'horario_fixo' | 'apos_mensagem';

export interface FollowupTemplate {
  id: string;
  campaign_id: string;
  sequencia: number;
  titulo: string;
  conteudo: string;
  tipo_mensagem: 'text' | 'image' | 'audio';
  intervalo_dias: number;
  intervalo_minutos: number;
  ativo: boolean;
  gerado_por_ia: boolean;
  ia_modelo: string | null;
  imagem_object_key: string | null;
  imagem_bucket: string | null;
  imagem_mimetype: string | null;
  imagem_nome_arquivo: string | null;
  created_at?: string;
  updated_at?: string;
  // Motivo: a API arquiva configurações sem apagar execuções históricas ligadas ao template.
  archived_at?: string | null;
  archived_by_api_credential_id?: string | null;
  template_oficial_id?: string | null;
  mapeamento_variaveis?: string[];
}

export interface FollowupCampaign {
  id: string;
  clinica_id: string | null;
  nome: string;
  descricao: string | null;
  tipo: 'automatico' | 'manual';
  ativo: boolean;
  dias_inatividade: number;
  janela_inatividade_minutos: number;
  janela_maxima_inatividade_minutos: number | null;
  horario_envio: string | null;
  horario_limite_envio: string | null;
  modo_envio: FollowupModoEnvio;
  apenas_dias_uteis: boolean;
  fuso_horario: string;
  tipo_gatilho: FollowupTriggerType;
  padrao_gatilho_texto: string | null;
  padrao_match_type: FollowupMatchType;
  usa_ia: boolean;
  ia_prompt_base: string | null;
  auto_pausar_ao_responder: boolean;
  apenas_leads_anuncio: boolean;
  ad_name_filtro: string | null;
  apenas_link_personalizado: boolean;
  etapa_kanban_id_filtro?: string | null;
  // Etapas do funil cujos leads nunca recebem esta campanha.
  etapas_bloqueadas_ids?: string[] | null;
  // Quando true, lead com venda registrada não recebe esta campanha.
  ignorar_leads_com_venda?: boolean | null;
  cooldown_dias_relancamento: number;
  limite_diario: number;
  usar_classificacao_inteligente: boolean;
  frases_sem_followup: string[] | null;
  is_preset: boolean;
  preset_slug: string | null;
  preset_categoria: string | null;
  follow_up_templates?: FollowupTemplate[];
  ativada_em?: string | null;
  regras_atualizadas_em?: string | null;
  created_at?: string;
  updated_at?: string;
  // Motivo: campanhas arquivadas deixam de aparecer/rodar, mas preservam métricas e histórico.
  archived_at?: string | null;
  archived_by_api_credential_id?: string | null;
}

export interface FollowupWizardTemplateDraft {
  id?: string;
  titulo: string;
  conteudo: string;
  intervalo_minutos: number;
  // 'manual' = texto fixo escrito pelo usuário
  // 'ai_dinamico' = IA lê o histórico do chat na hora do disparo e gera a mensagem
  mensagem_tipo: FollowupMensagemTipo;
  gerado_por_ia?: boolean;
  imagem_object_key?: string;
  imagem_bucket?: string;
  imagem_mimetype?: string;
  imagem_nome_arquivo?: string;
  imagem_preview_url?: string;
  template_oficial_id?: string;
  mapeamento_variaveis?: string[];
}

export interface FollowupCampaignDraft {
  nome: string;
  descricao: string;
  tipo: 'automatico' | 'manual';
  tipo_gatilho: FollowupTriggerType;
  padrao_gatilho_texto: string;
  padrao_match_type: FollowupMatchType;
  janela_inatividade_minutos: number;
  janela_maxima_inatividade_minutos: number;
  horario_envio: string;
  horario_limite_envio: string;
  modo_envio: FollowupModoEnvio;
  apenas_dias_uteis: boolean;
  auto_pausar_ao_responder: boolean;
  apenas_leads_anuncio: boolean;
  ad_name_filtro: string;
  apenas_link_personalizado: boolean;
  filtrar_por_etapa_kanban: boolean;
  etapa_kanban_id_filtro: string;
  etapas_bloqueadas_ids: string[];
  ignorar_leads_com_venda: boolean;
  cooldown_dias_relancamento: number;
  limite_diario: number;
  usar_classificacao_inteligente: boolean;
  frases_sem_followup: string;
  usa_ia: boolean;
  ia_prompt_base: string;
  templates: FollowupWizardTemplateDraft[];
}

export interface FollowupExecution {
  id: string;
  lead_id: string;
  campaign_id: string;
  template_id: string;
  tipo_execucao: 'automatico' | 'manual';
  status: 'pendente' | 'enviado' | 'erro' | 'cancelado' | 'cancelado_resposta' | 'cancelado_cooldown';
  data_agendada: string;
  data_enviado?: string | null;
  mensagem_id?: string | null;
  erro_detalhes?: string | null;
  triggered_by_message_id?: string | null;
  responded_at?: string | null;
  responded_message_id?: string | null;
  tentativas_envio?: number | null;
  ultimo_erro_em?: string | null;
  proxima_tentativa_em?: string | null;
  user_id_manual?: string | null;
  created_at?: string;
  updated_at?: string;
}

export const minutosParaLegivel = (minutos: number): string => {
  if (minutos < 60) return `${minutos} min`;
  if (minutos < 1440) return `${Math.round(minutos / 60)} h`;
  return `${Math.round(minutos / 1440)} dias`;
};
